// ─── 🪪 La tessera associativa e il certificato medico, nella scheda del socio ─
//
// 16/10/2026, mockup «Tessera associativa» (v9): «✅ Valida fino al 15/10/2027 · modulo
// compilato il 15/10/2026 alle 12:34 (mail WindDoc)», con «Correggi la data» e
// «Togli la tessera». Correggere non manda avvisi al socio.
//
// 📄 23/10/2026, mockup «Tessera e certificato medico» (*«1A 2sì 3A 4A 5no 6A»*, la 7 B): il pagamento dei 15 €
// («💶 Incassa»: contanti o carta) e il certificato medico, con «📄 Apri», «✅ Va bene», «✏️ Correggi la
// data» e «Chiedi un'altra foto». La foto si apre solo per chi vede l'anagrafica: è un dato di salute.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MASSIMO_CERTIFICATO,
  TIPI_DEL_CERTIFICATO,
  euroDellaTessera,
  type ControllaCertificato,
  type TesseraDelSocio as Tessera,
} from '@pmo/shared';
import { apriFile, chiama } from '../../lib/api.js';
import { Guasto } from '../../components/pezzi.js';

const giorno = (d: string) => d.split('-').reverse().join('/');
const giornoDi = (iso: string) => new Date(iso).toLocaleDateString('it-IT');
const MEZZI: Record<string, string> = { cash: 'contanti', card: 'carta', wallet: 'Borsellino' };

export function TesseraDelSocio({
  id,
  modificabile,
  puoIncassare,
}: {
  id: string;
  modificabile: boolean;
  puoIncassare: boolean;
}): React.JSX.Element | null {
  const qc = useQueryClient();
  const [apre, setApre] = useState(false);
  const [data, setData] = useState('');
  // 🔑 Una chiave per questa scheda aperta: due tocchi sullo stesso bottone sono lo stesso incasso.
  const [chiaveIncasso] = useState(() => crypto.randomUUID());
  const t = useQuery({
    queryKey: ['tessera-socio', id],
    queryFn: () => chiama<Tessera>('GET', `/members/${id}/tessera`),
  });
  const aggiorna = (dopo: Tessera) => qc.setQueryData(['tessera-socio', id], dopo);
  const scrivi = useMutation({
    meta: {
      fatto: (_d: unknown, v: unknown) => (v === null ? 'tessera tolta' : 'data della tessera corretta'),
    },
    mutationFn: (scade: string | null) => chiama<Tessera>('PUT', `/members/${id}/tessera`, { scade }),
    onSuccess: (dopo) => {
      aggiorna(dopo);
      setApre(false);
    },
  });
  const incassa = useMutation({
    meta: { fatto: 'tessera incassata' },
    mutationFn: (method: 'cash' | 'card') =>
      chiama<Tessera>('POST', `/members/${id}/tessera/incassa`, { method, idempotencyKey: chiaveIncasso }),
    onSuccess: aggiorna,
  });
  if (!t.data) return <Guasto errore={t.error} />;
  const x = t.data;
  const dal = x.dal ? new Date(x.dal) : null;
  const prezzo = euroDellaTessera(x.prezzoCents);

  return (
    <section className="riquadro">
      <h3>🪪 Tessera e certificato medico</h3>
      {x.inPausa && (
        <p className="avvertenza piccolo">
          ⏸️ Le prenotazioni dal bot sono in pausa: {pausaPerche(x)}. Dalla Segreteria si prenota lo stesso.
        </p>
      )}
      <p className="piccolo">
        {x.stato === 'da_fare' && 'Tessera da fare.'}
        {x.stato !== 'da_fare' && x.scade && (
          <>
            {x.stato === 'attiva' ? `✅ Valida fino al ${giorno(x.scade)}` : `Scaduta il ${giorno(x.scade)}`}
            {dal &&
              x.fonte === 'mail' &&
              ` · modulo compilato il ${dal.toLocaleDateString('it-IT')} alle ${dal.toLocaleTimeString(
                'it-IT',
                {
                  hour: '2-digit',
                  minute: '2-digit',
                },
              )} (mail WindDoc)`}
            {x.fonte === 'segreteria' && ' · data scritta dalla Segreteria'}
          </>
        )}
      </p>
      <p className="piccolo">
        {x.pagata && x.pagamento && (
          <>
            💶 Pagata{x.pagataIl ? ` il ${giorno(x.pagataIl)}` : ''}:{' '}
            {euroDellaTessera(x.pagamento.importoCents)}, {MEZZI[x.pagamento.metodo] ?? x.pagamento.metodo}
            {x.pagamento.note?.includes('(simulato)') ? ' dal bot (simulato)' : ''}
          </>
        )}
        {x.pagata && !x.pagamento && '💶 Fatta gratis, prima dei 15 €: si paga al rinnovo.'}
        {!x.pagata && x.stato === 'attiva' && `💶 ${prezzo} da pagare: dal bot, oppure qui.`}
        {!x.pagata &&
          x.stato !== 'attiva' &&
          `💶 ${prezzo} l’anno: il socio paga dal bot dopo il modulo, oppure qui.`}
      </p>
      {puoIncassare && !x.pagata && (
        <p>
          <span className="tenue piccolo">Incassa {prezzo}: </span>
          {(['cash', 'card'] as const).map((m) => (
            <span key={m}>
              <button
                type="button"
                className="bordato piccolo"
                disabled={incassa.isPending}
                onClick={() => incassa.mutate(m)}
              >
                {m === 'cash' ? '💶 Contanti' : '💳 Carta'}
              </button>{' '}
            </span>
          ))}
        </p>
      )}
      <Guasto errore={incassa.error} />
      {modificabile && !apre && (
        <p>
          <button
            type="button"
            className="bordato piccolo"
            onClick={() => {
              setData(x.scade ?? '');
              setApre(true);
            }}
          >
            Correggi la data
          </button>{' '}
          {x.scade && (
            <button
              type="button"
              className="bordato piccolo"
              disabled={scrivi.isPending}
              onClick={() => scrivi.mutate(null)}
            >
              Togli la tessera
            </button>
          )}
        </p>
      )}
      {apre && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (data) scrivi.mutate(data);
          }}
        >
          <label className="campo">
            <span>Valida fino al</span>
            <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
          </label>
          <button type="submit" disabled={!data || scrivi.isPending}>
            Salva
          </button>{' '}
          <button type="button" className="bordato" onClick={() => setApre(false)}>
            Lascia stare
          </button>
        </form>
      )}
      <Guasto errore={scrivi.error} />
      <Certificato id={id} x={x} modificabile={modificabile} aggiorna={aggiorna} />
    </section>
  );
}

function pausaPerche(x: Tessera): string {
  if (x.certificato.stato === 'scaduto')
    return `certificato medico scaduto il ${giorno(x.certificato.validoFino!)}`;
  if (x.certificato.stato === 'altra_foto') return 'chiesta un’altra foto del certificato medico';
  return `il certificato medico serviva entro il ${giorno(x.certificatoEntroIl!)}`;
}

const STATI: Record<Tessera['certificato']['stato'], string> = {
  manca: 'Da mandare',
  da_controllare: '🔎 Da controllare',
  controllato: '✅ Controllato',
  altra_foto: '📄 Chiesta un’altra foto',
  scaduto: 'Scaduto',
};

function Certificato({
  id,
  x,
  modificabile,
  aggiorna,
}: {
  id: string;
  x: Tessera;
  modificabile: boolean;
  aggiorna: (t: Tessera) => void;
}): React.JSX.Element {
  const c = x.certificato;
  const [modo, setModo] = useState<'niente' | 'data' | 'altra' | 'carica'>('niente');
  const [rilascio, setRilascio] = useState('');
  const [motivo, setMotivo] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [errApri, setErrApri] = useState<unknown>(null);
  const controlla = useMutation({
    meta: {
      fatto: (_d: unknown, v: unknown) =>
        (v as ControllaCertificato).esito === 'va_bene'
          ? 'certificato controllato'
          : (v as ControllaCertificato).esito === 'data'
            ? 'data di rilascio corretta'
            : 'chiesta un’altra foto al socio',
    },
    mutationFn: (e: ControllaCertificato) =>
      chiama<Tessera>('POST', `/members/${id}/certificato/controlla`, e),
    onSuccess: (dopo) => {
      aggiorna(dopo);
      setModo('niente');
    },
  });
  const carica = useMutation({
    meta: { fatto: 'certificato caricato' },
    mutationFn: async () => {
      const f = file!;
      const fileBase64 = await new Promise<string>((ok, no) => {
        const r = new FileReader();
        r.onload = () => ok(String(r.result).replace(/^data:[^,]*,/, ''));
        r.onerror = () => no(r.error);
        r.readAsDataURL(f);
      });
      return chiama<Tessera>('POST', `/members/${id}/certificato`, {
        fileBase64,
        mime: f.type,
        fileName: f.name,
        issuedOn: rilascio,
      });
    },
    onSuccess: (dopo) => {
      aggiorna(dopo);
      setModo('niente');
      setFile(null);
    },
  });
  const tipoBuono = file !== null && (TIPI_DEL_CERTIFICATO as readonly string[]).includes(file.type);
  const grandezzaBuona = file !== null && file.size <= MASSIMO_CERTIFICATO;

  return (
    <div>
      <h4>📄 Certificato medico</h4>
      <p className="piccolo">
        {STATI[c.stato]}
        {c.arrivato &&
          ` · arrivato il ${giornoDi(c.arrivato)} ${c.fonte === 'bot' ? 'dal bot' : 'dalla Segreteria'}`}
        {c.rilascio && ` · rilasciato il ${giorno(c.rilascio)}`}
        {c.validoFino && ` · valido fino al ${giorno(c.validoFino)}`}
        {c.stato === 'altra_foto' && c.nota && ` · motivo: «${c.nota}»`}
        {c.stato === 'manca' && x.certificatoEntroIl && ` · serve entro il ${giorno(x.certificatoEntroIl)}`}
      </p>
      {modificabile && modo === 'niente' && (
        <p>
          {c.arrivato && (
            <>
              <button
                type="button"
                className="bordato piccolo"
                onClick={() => {
                  setErrApri(null);
                  apriFile(`/members/${id}/certificato/file`).catch(setErrApri);
                }}
              >
                📄 Apri
              </button>{' '}
            </>
          )}
          {c.stato === 'da_controllare' && (
            <>
              <button
                type="button"
                className="piccolo"
                disabled={controlla.isPending}
                onClick={() => controlla.mutate({ esito: 'va_bene' })}
              >
                ✅ Va bene
              </button>{' '}
            </>
          )}
          {c.rilascio && (
            <>
              <button
                type="button"
                className="bordato piccolo"
                onClick={() => {
                  setRilascio(c.rilascio ?? '');
                  setModo('data');
                }}
              >
                ✏️ Correggi la data
              </button>{' '}
            </>
          )}
          {(c.stato === 'da_controllare' || c.stato === 'controllato') && (
            <>
              <button type="button" className="bordato piccolo" onClick={() => setModo('altra')}>
                Chiedi un’altra foto
              </button>{' '}
            </>
          )}
          <button
            type="button"
            className="bordato piccolo"
            onClick={() => {
              setRilascio('');
              setModo('carica');
            }}
          >
            📤 Carica il certificato
          </button>
        </p>
      )}
      {modo === 'data' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (rilascio) controlla.mutate({ esito: 'data', rilascio });
          }}
        >
          <label className="campo">
            <span>Data di rilascio (vale un anno)</span>
            <input type="date" value={rilascio} onChange={(e) => setRilascio(e.target.value)} />
          </label>
          <button type="submit" disabled={!rilascio || controlla.isPending}>
            Salva
          </button>{' '}
          <button type="button" className="bordato" onClick={() => setModo('niente')}>
            Lascia stare
          </button>
        </form>
      )}
      {modo === 'altra' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (motivo.trim().length >= 3) controlla.mutate({ esito: 'altra_foto', motivo: motivo.trim() });
          }}
        >
          <label className="campo">
            <span>Il motivo, lo legge il socio</span>
            <input
              value={motivo}
              placeholder="la data non si legge"
              maxLength={200}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          <button type="submit" disabled={motivo.trim().length < 3 || controlla.isPending}>
            Manda al socio
          </button>{' '}
          <button type="button" className="bordato" onClick={() => setModo('niente')}>
            Lascia stare
          </button>
        </form>
      )}
      {modo === 'carica' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (tipoBuono && grandezzaBuona && rilascio) carica.mutate();
          }}
        >
          <label className="campo">
            <span>La foto o il PDF (fino a 8 MB)</span>
            <input
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
          {file && !tipoBuono && <p className="guasto">Serve una foto JPG o PNG, oppure un PDF.</p>}
          {file && tipoBuono && !grandezzaBuona && <p className="guasto">Il file supera 8 MB.</p>}
          <label className="campo">
            <span>Data di rilascio (vale un anno)</span>
            <input type="date" value={rilascio} onChange={(e) => setRilascio(e.target.value)} />
          </label>
          <button type="submit" disabled={!tipoBuono || !grandezzaBuona || !rilascio || carica.isPending}>
            {carica.isPending ? 'Un attimo…' : 'Carica'}
          </button>{' '}
          <button type="button" className="bordato" onClick={() => setModo('niente')}>
            Lascia stare
          </button>
        </form>
      )}
      <Guasto errore={controlla.error ?? carica.error ?? errApri} />
    </div>
  );
}
